import { useEffect, useMemo, useState, type CSSProperties } from "react";

import {
  AIRSHOW_REPRESENTATION_CONTRACTS,
  airshowWorkContract,
  summarizeAirshowRepresentations,
  summarizeAirshowWork,
  type AirshowRepresentation,
  type AirshowRepresentationRow,
} from "./airshowRepresentationModel";
import { AirshowAssemblySemanticsDemo } from "./AirshowAssemblySemanticsDemo";
import { AirshowControlImportDemo } from "./AirshowControlImportDemo";
import { AirshowCoverageComparison } from "./AirshowCoverageComparison";
import "./airshow-representations.css";

type CapabilityFilter = "viewable" | "all" | "exact" | "editable" | "both" | "unresolved";
type CoverageSection = "gallery" | "inspector" | "backlog";

interface AirshowRepresentationPageProps {
  rows: readonly AirshowRepresentationRow[];
  reportRevision: string;
  fullFleet: boolean;
  onInspectModel?: (entryId: string) => void;
  comparisonProjectId?: string;
  comparisonBranch?: string;
}

function count(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

function shortRevision(value: string): string {
  return value.length > 28 ? `${value.slice(0, 20)}…${value.slice(-8)}` : value;
}

function representationLabel(row: AirshowRepresentationRow): string {
  return AIRSHOW_REPRESENTATION_CONTRACTS.find(({ id }) => id === row.representation)?.label
    ?? row.representation;
}

function preferredRow(rows: readonly AirshowRepresentationRow[]): AirshowRepresentationRow | null {
  return rows.find((row) => isExactAndEditable(row))
    ?? rows.find((row) => isEditable(row))
    ?? rows.find((row) => isExact(row))
    ?? rows.find((row) => isViewable(row))
    ?? rows[0]
    ?? null;
}

function isViewable(row: AirshowRepresentationRow): boolean {
  return row.renderable && row.representation !== "unresolved";
}

function isExact(row: AirshowRepresentationRow): boolean {
  return isViewable(row) && row.exactAttested;
}

function isEditable(row: AirshowRepresentationRow): boolean {
  return isViewable(row) && row.semanticComplete;
}

function isExactAndEditable(row: AirshowRepresentationRow): boolean {
  return isExact(row) && isEditable(row);
}

function matchesCapability(row: AirshowRepresentationRow, filter: CapabilityFilter): boolean {
  if (filter === "viewable") return isViewable(row);
  if (filter === "exact") return isExact(row);
  if (filter === "editable") return isEditable(row);
  if (filter === "both") return isExactAndEditable(row);
  if (filter === "unresolved") return !isViewable(row);
  return true;
}

function StatusBadge({ children, state }: { children: string; state: "pass" | "warn" | "fail" }) {
  return <span className="airshow-rep-badge" data-state={state}>{children}</span>;
}

function AircraftList({ rows, selected, onSelect }: {
  rows: readonly AirshowRepresentationRow[];
  selected: AirshowRepresentationRow | null;
  onSelect: (entryId: string) => void;
}) {
  return <section className="airshow-representations-list" data-testid="airshow-representation-list">
    <header><strong>{count(rows.length)} aircraft</strong><span>Select to inspect</span></header>
    <div>{rows.map((row) => {
      const exact = isExact(row);
      const editable = isEditable(row);
      return <button
      aria-pressed={selected?.entryId === row.entryId}
      data-kind={row.representation}
      data-testid="airshow-representation-row"
      key={row.entryId}
      onClick={() => onSelect(row.entryId)}
      type="button"
    >
      <span><strong>{row.label}</strong><code>{row.entryId}</code></span>
      <i>{representationLabel(row)}</i>
      <span className="airshow-rep-row-badges">
        <StatusBadge state={row.renderable ? "pass" : "fail"}>{row.renderable ? "Display" : "No display"}</StatusBadge>
        <StatusBadge state={exact ? "pass" : "warn"}>{exact ? "Exact" : row.exactAttested ? "Exact evidence only" : "Exact gated"}</StatusBadge>
        <StatusBadge state={editable ? "pass" : "warn"}>{editable ? "Editable" : row.semanticComplete ? "Semantics only" : "Read only"}</StatusBadge>
      </span>
    </button>})}</div>
    {rows.length === 0 ? <p>No aircraft matches these evidence filters.</p> : null}
  </section>;
}

function CapabilityMatrix({ row }: { row: AirshowRepresentationRow }) {
  const viewable = isViewable(row);
  const exact = isExact(row);
  const editable = isEditable(row);
  const inventory = `${row.realizedComponentCount ?? "—"} / ${row.sourceComponentCount ?? "—"} components · ${row.realizedSurfaceCount ?? "—"} / ${row.sourceSurfaceCount ?? "—"} surfaces`;
  return <table aria-label="Selected aircraft capability matrix" className="airshow-capability-matrix">
    <thead><tr><th>Capability</th><th>State</th><th>Evidence</th><th>Safe now</th><th>Still gated</th></tr></thead>
    <tbody>
      <tr>
        <th>Display</th><td><StatusBadge state={viewable ? "pass" : "fail"}>{viewable ? "Ready" : "Blocked"}</StatusBadge></td>
        <td>{inventory}</td><td>{viewable ? "Display, pick, and compare validated bodies" : "Inspect source-bound diagnostics"}</td>
        <td>{viewable ? "No display inventory gap" : "Scene insertion, measurement, and analysis"}</td>
      </tr>
      <tr>
        <th>Exact geometry</th><td><StatusBadge state={exact ? "pass" : "warn"}>{exact ? "Attested" : row.exactAttested ? "Evidence only" : "Gated"}</StatusBadge></td>
        <td><code>{row.exactCode}</code></td><td>{exact ? "Surface evaluation, derivatives, and exact-control workflows" : "No exact-only operation is enabled"}</td>
        <td>{row.blockedOperations.join(" · ")}</td>
      </tr>
      <tr>
        <th>Editable semantics</th><td><StatusBadge state={editable ? "pass" : "warn"}>{editable ? "Supported" : row.semanticComplete ? "Semantics only" : "Incomplete"}</StatusBadge></td>
        <td><code>{row.semanticCode}</code></td><td>{editable ? "Supported named-primitive authoring under this receipt" : "Read-only inspection of reconstructed declarations"}</td>
        <td>{editable ? "Operations outside the reconstructed declarations" : row.semanticComplete ? "Display repair before aircraft editing" : "Named source-parameter and topology-changing edits"}</td>
      </tr>
    </tbody>
  </table>;
}

export function AirshowRepresentationPage({
  rows, reportRevision, fullFleet, onInspectModel, comparisonProjectId, comparisonBranch = "main",
}: AirshowRepresentationPageProps) {
  const [section, setSection] = useState<CoverageSection>("gallery");
  const [search, setSearch] = useState("");
  const [representation, setRepresentation] = useState<"all" | AirshowRepresentation>("all");
  const [capability, setCapability] = useState<CapabilityFilter>("viewable");
  const [selectedId, setSelectedId] = useState<string | null>(() => preferredRow(rows)?.entryId ?? null);
  const [proofLabOpen, setProofLabOpen] = useState(false);
  const summary = useMemo(() => summarizeAirshowRepresentations(rows), [rows]);
  const workSummary = useMemo(() => summarizeAirshowWork(rows), [rows]);
  const representationCounts = useMemo(() => new Map(
    AIRSHOW_REPRESENTATION_CONTRACTS.map((contract) => [
      contract.id, rows.filter((row) => row.representation === contract.id).length,
    ]),
  ), [rows]);
  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return rows.filter((row) => matchesCapability(row, capability)
      && (representation === "all" || row.representation === representation)
      && (!query || `${row.label} ${row.entryId} ${row.archiveMemberPath} ${row.exactCode}`
        .toLocaleLowerCase().includes(query)));
  }, [capability, representation, rows, search]);
  const selected = filtered.find((row) => row.entryId === selectedId) ?? preferredRow(filtered);

  useEffect(() => {
    if (selected?.entryId === selectedId) return;
    setSelectedId(selected?.entryId ?? null);
  }, [selected, selectedId]);

  const chooseSection = (next: CoverageSection) => {
    setSection(next);
    if (next !== "inspector") setProofLabOpen(false);
  };

  return <section aria-labelledby="airshow-representations-title" className="airshow-representations" data-testid="airshow-representation-page">
    <header className="airshow-representations-hero">
      <div><span>Airshow · Loom capability</span><h2 id="airshow-representations-title">Aircraft that work—and what “work” means</h2><p>Exact shape is not editability. Loom evaluates display, geometric exactness, and aircraft meaning as independent evidence gates.</p></div>
      <div className="airshow-representations-scope" data-testid="airshow-representation-scope">
        <StatusBadge state={fullFleet ? "pass" : "warn"}>{fullFleet ? `Full ${count(summary.total)}-aircraft report` : "Partial selected run"}</StatusBadge>
        <code title={reportRevision}>{shortRevision(reportRevision)}</code>
        <p>{fullFleet ? "One terminal, source-bound report." : "Treat these as selected-run counts, not fleet totals."}</p>
      </div>
    </header>

    <section className="airshow-representations-summary" data-testid="airshow-representation-summary">
      <article data-tone="viewable"><span>Viewable</span><strong>{count(summary.viewable)}</strong><p>complete validated display</p></article>
      <article data-tone="exact"><span>Exact geometry</span><strong>{count(summary.exactAttested)}</strong><p>attested surface shape · not editability</p></article>
      <article data-tone="semantic"><span>Semantically editable</span><strong>{count(summary.semanticComplete)}</strong><p>supported aircraft meaning · shape independent</p></article>
      <article data-tone="both"><span>Exact + editable</span><strong>{count(summary.exactAndSemantic)}</strong><p>both evidence gates pass</p></article>
    </section>

    <p className="airshow-representations-explainer">Of {count(summary.exactAttested)} exact-shape aircraft, {count(summary.exactAndSemantic)} also carry editable semantics. {count(summary.semanticComplete - summary.exactAndSemantic)} editable aircraft currently use a non-exact display representation.</p>

    <nav aria-label="Airshow coverage sections" className="airshow-representations-nav">
      <button aria-pressed={section === "gallery"} onClick={() => chooseSection("gallery")} type="button">Aircraft gallery &amp; visual comparison</button>
      <button aria-pressed={section === "inspector"} onClick={() => chooseSection("inspector")} type="button">Editability / capability inspector</button>
      <button aria-pressed={section === "backlog"} onClick={() => chooseSection("backlog")} type="button">Engineering backlog</button>
    </nav>

    {section !== "backlog" ? <div className="airshow-representations-filters">
      <label><span>Search aircraft</span><input aria-label="Search aircraft" onChange={(event) => setSearch(event.currentTarget.value)} placeholder="Boeing 777, FL52, closure…" type="search" value={search} /></label>
      <label><span>Capability</span><select aria-label="Capability" onChange={(event) => setCapability(event.currentTarget.value as CapabilityFilter)} value={capability}>
        <option value="viewable">Viewable aircraft</option><option value="all">All aircraft</option><option value="exact">Exact geometry</option><option value="editable">Semantically editable</option><option value="both">Exact + editable</option><option value="unresolved">Display repair needed</option>
      </select></label>
      <label><span>Representation</span><select aria-label="Representation" onChange={(event) => setRepresentation(event.currentTarget.value as "all" | AirshowRepresentation)} value={representation}>
        <option value="all">All representations</option>{AIRSHOW_REPRESENTATION_CONTRACTS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
      </select></label>
    </div> : null}

    {section === "gallery" ? <section className="airshow-representations-mode" data-testid="airshow-gallery-mode">
      <header><span>01 · What works first</span><h3>Aircraft gallery &amp; visual comparison</h3><p>Viewable aircraft lead by default. Select one to compare the source reference with Loom’s validated display.</p></header>
      <div className="airshow-gallery-grid"><AircraftList onSelect={setSelectedId} rows={filtered} selected={selected} /><div className="airshow-gallery-comparison">{selected && comparisonProjectId ? <AirshowCoverageComparison branch={comparisonBranch} projectId={comparisonProjectId} row={selected} /> : selected ? <div className="airshow-comparison-placeholder"><span>Selected aircraft</span><h3>{selected.label}</h3><p>Visual comparison needs a project-scoped review endpoint. The fleet evidence remains available in the capability inspector.</p></div> : <div className="airshow-comparison-placeholder"><p>No aircraft matches these filters.</p></div>}</div></div>
    </section> : null}

    {section === "inspector" ? <section className="airshow-representations-mode" data-testid="airshow-inspector-mode">
      <header><span>02 · Selected aircraft</span><h3>Editability / capability inspector</h3><p>One matrix separates what Loom can display, reproduce exactly, and safely author.</p></header>
      <div className="airshow-inspector-grid"><AircraftList onSelect={setSelectedId} rows={filtered} selected={selected} />{selected ? <aside className="airshow-representations-detail" data-kind={selected.representation} data-testid="airshow-representation-detail">
        <header><div><span>{representationLabel(selected)}</span><h3>{selected.label}</h3><code>{selected.entryId}</code></div><div><StatusBadge state={isViewable(selected) ? "pass" : "fail"}>{isViewable(selected) ? "Display ready" : "Display blocked"}</StatusBadge><StatusBadge state={isExact(selected) ? "pass" : "warn"}>{isExact(selected) ? "Exact-attested" : selected.exactAttested ? "Exact evidence only" : "Exact gated"}</StatusBadge><StatusBadge state={isEditable(selected) ? "pass" : "warn"}>{isEditable(selected) ? "Editable" : selected.semanticComplete ? "Semantics only" : "Semantic work remains"}</StatusBadge></div></header>
        <CapabilityMatrix row={selected} />
        <details className="airshow-source-evidence"><summary>Source-bound evidence and diagnostics</summary><div><dl><div><dt>Archive member</dt><dd>{selected.archiveMemberPath}</dd></div><div><dt>Display representation</dt><dd>{selected.displayRepresentation ?? "none"}</dd></div><div><dt>Display fidelity</dt><dd>{selected.displayFidelity ?? "unavailable"}</dd></div><div><dt>Exact outcome</dt><dd>{selected.exactCode}</dd></div><div><dt>Semantic outcome</dt><dd>{selected.semanticCode}</dd></div></dl>{!selected.renderable ? <p>{selected.displayCode} · {selected.displayMessage}</p> : null}{selected.semanticRejections.length ? <ul>{selected.semanticRejections.map((item) => <li key={`${item.geom_id}:${item.feature_id}:${item.parameter}`}>{item.feature_id} · {item.parameter} · {item.occurrence_count} occurrence{item.occurrence_count === 1 ? "" : "s"}</li>)}</ul> : null}{selected.unsupportedDeclarations.length ? <p>Unsupported declarations · {selected.unsupportedDeclarations.join(", ")}</p> : null}</div></details>
        <div className="airshow-detail-actions">{onInspectModel ? <button className="airshow-representations-inspect" onClick={() => onInspectModel(selected.entryId)} type="button">Inspect in Compare</button> : null}<button className="airshow-proof-lab-button" onClick={() => setProofLabOpen((open) => !open)} type="button">{proofLabOpen ? "Close primitive proof lab" : "Open primitive proof lab"}</button></div>
      </aside> : <div className="airshow-comparison-placeholder"><p>No aircraft matches these filters.</p></div>}</div>
      {proofLabOpen ? <section className="airshow-representations-proof-lab" data-testid="airshow-proof-lab"><header><span>Separate prototype surface</span><h3>Primitive proof lab</h3><p>These bounded demonstrators prove assembly and control behavior. They do not change the selected aircraft’s fleet classification.</p></header><AirshowAssemblySemanticsDemo /><AirshowControlImportDemo /></section> : null}
    </section> : null}

    {section === "backlog" ? <section className="airshow-representations-mode airshow-representations-backlog" data-testid="airshow-backlog-mode">
      <header><span>03 · Work remaining</span><h3>Engineering backlog</h3><p>Representation repair and semantic authoring are separate workloads. Counts remain tied to this report.</p></header>
      <section><h4>Representation outcomes</h4><div className="airshow-representation-contract-grid">{AIRSHOW_REPRESENTATION_CONTRACTS.map((contract) => <article data-kind={contract.id} data-testid="airshow-representation-contract" key={contract.id}><span>{contract.eyebrow}</span><strong>{contract.label}</strong><b>{count(representationCounts.get(contract.id) ?? 0)}</b><p>{contract.meaning}</p></article>)}</div></section>
      <section><h4>Path to editable aircraft</h4><div className="airshow-representations-plan-grid"><article><b>01</b><div><strong>Canonical Aircraft IR</strong><p>Bind source identity, occurrences, bodies, pose, representation receipts, semantics, and operation capabilities.</p></div></article><article><b>02</b><div><strong>Hierarchy and attachments</strong><p>Reconstruct parent transforms, surface attachments, symmetry, and component ownership without multiplying frames.</p></div></article><article><b>03</b><div><strong>First-class primitives</strong><p>Complete wings, bodies, propulsors, subsurfaces, controls, and modifiers as source-bound definitions.</p></div></article><article><b>04</b><div><strong>Exact operation receipts</strong><p>Prove topology, closure, trims, mixed-representation operations, export order, and round-trip provenance.</p></div></article></div></section>
      <section><h4>Exact-lane work packages</h4><div className="airshow-representations-work">{workSummary.map((item) => <article key={item.id}><span><strong>{item.label}</strong><b>{count(item.count)}</b></span><i style={{ "--work-share": `${summary.total ? item.count / summary.total * 100 : 0}%` } as CSSProperties} /><p>{airshowWorkContract(item.id).meaning}</p></article>)}</div></section>
    </section> : null}
  </section>;
}
